import React, { useState, useEffect, useRef } from 'react';
import Hero from './components/Hero';
import ImageInput from './components/ImageInput';
import BatchUpload from './components/BatchUpload';
import CardSlab from './components/CardSlab';
import ListingModal from './components/ListingModal';
import SkeletonSlab from './components/SkeletonSlab';
// import SignIn from './components/SignIn'; // Replaced by Amplify
import { analyzeBookshelf, identifyBookByText } from './services/geminiService';
import { SportsCard, AnalysisState } from './types';
import { Layers, Grid, Search as SearchIcon, Loader2, Box, LogOut, Star, Scan, X, Sparkles, Utensils, GraduationCap, Terminal } from 'lucide-react';
import { AntiSlop } from './components/AntiSlop';
import { GameMaker } from './components/GameMaker';
import { HashRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';


// AWS Amplify
import { Amplify } from 'aws-amplify';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { signIn } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';

// Configure Auth
Amplify.configure({
    Auth: {
        Cognito: {
            userPoolId: 'ca-central-1_ckQmeD5en',
            userPoolClientId: '2tu5ghqa33d2tdq70c6dg8c7f6',
        }
    }
});

// Hardcoded Backend URL for V8 stability
const BACKEND_URL = "https://1mwkkhdk52.execute-api.ca-central-1.amazonaws.com";

const AppContent: React.FC = () => {
    const { user, signOut } = useAuthenticator((context) => [context.user]);

    // Auth State Check for Render
    // If not authenticated, we still render AppContent to show the Guest UI

    const [cards, setCards] = useState<SportsCard[]>([]);
    const [analysisState, setAnalysisState] = useState<AnalysisState>({
        status: 'idle',
        message: '',
        progress: 0,
        ocrText: []
    });
    const [selectedCardForListing, setSelectedCardForListing] = useState<SportsCard | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showAuth, setShowAuth] = useState(false);

    // Shelf State
    const [savedCards, setSavedCards] = useState<SportsCard[]>(() => {
        if (typeof window === 'undefined') return [];
        try {
            const saved = localStorage.getItem('slabLogic_savedCards');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            console.error('Failed to parse saved cards', e);
            return [];
        }
    });

    const [shelfSearchQuery, setShelfSearchQuery] = useState('');
    const [isSyncing, setIsSyncing] = useState(false);

    // Set UserID from Amplify or localStorage (VIP bypass)
    // Check for VIP URL param first
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const vipCode = urlParams.get('vip');
        if (vipCode === 'slab2026') {
            localStorage.setItem('slab_user_id', 'VIP_USER');
            // Remove query param from URL for cleanliness
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    // Priority: Amplify user > localStorage (VIP) > generate guest ID
    const userId = user?.username || user?.userId || (() => {
        if (typeof window === 'undefined') return 'guest';
        let storedId = localStorage.getItem('slab_user_id');
        if (!storedId) {
            storedId = `guest_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
            localStorage.setItem('slab_user_id', storedId);
        }
        return storedId;
    })();

    // Sync Function (Only sync to cloud if authenticated user or VIP bypass)
    const handleSync = async () => {
        if (!userId || isSyncing || (!user && userId.startsWith('guest_'))) return;
        setIsSyncing(true);
        try {
            if (savedCards.length > 0) {
                await fetch(`${BACKEND_URL}/sync`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId, books: savedCards, token: 'slablogic-alpha' })
                });
            }
            // Fetch Remote
            const res = await fetch(`${BACKEND_URL}/shelf?userId=${userId}`);
            const data = await res.json();

            if (data.books) {
                const remoteCards = data.books as SportsCard[];
                const localIds = new Set(savedCards.map(b => b.id));
                const newRemote = remoteCards.filter(b => !localIds.has(b.id));

                if (newRemote.length > 0) {
                    setSavedCards(prev => [...prev, ...newRemote]);
                }
            }
        } catch (e) {
            console.warn("Cloud Sync offline. Local storage active.");
        } finally {
            setIsSyncing(false);
        }
    };

    // Auto-sync on load
    useEffect(() => {
        if (userId) handleSync();
    }, [userId]);

    const location = useLocation();

    // Dynamic document title based on workspace route
    useEffect(() => {
        if (location.pathname.startsWith('/anti-slop')) {
            document.title = "Veritas Anti-Slop Filter";
        } else if (location.pathname.startsWith('/game-maker')) {
            document.title = "90s Style Game Engine";
        } else if (location.pathname.startsWith('/workspace')) {
            document.title = "SlabLogic Trading Cards";
        } else {
            document.title = "SlabLogic Venture Studio";
        }
    }, [location]);
    // Listing Tone Preference (MVP)
    const [listingTone, setListingTone] = useState<string>('Professional');

    // Persist shelf
    useEffect(() => {
        localStorage.setItem('slabLogic_savedCards', JSON.stringify(savedCards));
    }, [savedCards]);

    const handleError = (error: any, defaultMsg: string) => {
        console.error(error);
        setAnalysisState(prev => ({ ...prev, status: 'error', message: defaultMsg, progress: 0 }));
    };

    const [scanMode, setScanMode] = useState<'single' | 'batch'>('single');
    const [scanType, setScanType] = useState<'quick' | 'pro'>('quick');

    const createThumbnail = (base64Str: string): Promise<string> => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) { resolve(''); return; }
                const scale = 150 / img.width;
                canvas.width = 150;
                canvas.height = img.height * scale;
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
                resolve(dataUrl.split(',')[1]);
            };
            img.src = `data:image/jpeg;base64,${base64Str}`;
        });
    };

    const handleImageSelected = async (images: string[], mimeType: string) => {
        // TRIAL CHECK - Backend handles limits now, frontend just checks for VIP status
        const isGuest = !user;
        const isVIP = localStorage.getItem('slab_user_id') === 'VIP_USER';

        try {
            setCards([]);
            setAnalysisState({
                status: 'ocr',
                message: images.length > 1 ? 'Forensic Scan in Progress...' : 'Scanning Card...',
                progress: 10,
                ocrText: []
            });

            // Generate Thumbnail
            let thumb = '';
            if (images.length > 0) {
                thumb = await createThumbnail(images[0]);
            }

            // Analysis
            const results = await analyzeBookshelf(
                images,
                mimeType,
                [],
                (msg) => setAnalysisState(prev => ({ ...prev, message: msg })),
                listingTone,
                isGuest,
                scanType
            );

            // Trial tracking moved to backend (limit of 5 scans)

            const resultsWithThumb = results.map(b => ({ ...b, thumbnailUrl: thumb }));

            setCards(resultsWithThumb);
            setAnalysisState({
                status: 'complete',
                message: `Identified ${results.length} Item(s)`,
                progress: 100,
                ocrText: []
            });

        } catch (error: any) {
            handleError(error, 'Failed to analyze card. Please try again.');
        }
    };

    const handleManualSearch = async () => {
        if (!searchQuery.trim()) return;
        try {
            setAnalysisState({ status: 'searching', message: `Searching database for "${searchQuery}"...`, progress: 20 });
            setCards([]);
            const results = await identifyBookByText(searchQuery, (msg) => setAnalysisState(prev => ({ ...prev, message: msg })), listingTone);
            setCards(results);
            setAnalysisState({ status: 'complete', message: `Found ${results.length} items`, progress: 100 });
        } catch (error: any) {
            handleError(error, 'Search failed.');
        }
    };

    const toggleSaveCrad = (card: SportsCard) => {
        setSavedCards(prev => {
            const isSaved = prev.some(b => b.id === card.id);
            if (isSaved) return prev.filter(b => b.id !== card.id);
            else return [...prev, card];
        });
    };

    const getFilteredCollection = () => {
        if (!shelfSearchQuery.trim()) return savedCards;
        const q = shelfSearchQuery.toLowerCase();
        return savedCards.filter(b => b.player.toLowerCase().includes(q) || b.set.toLowerCase().includes(q));
    };

    // Limits check (MVP)
    const scanCount = useRef(0);
    // In real app, check this against User Attributes or DB

    const displayedCards = location.pathname.includes('/collection') ? getFilteredCollection() : cards;
    const isLoading = ['uploading', 'ocr', 'identifying', 'searching'].includes(analysisState.status);

    const [showPricing, setShowPricing] = useState(false);
    const [appToast, setAppToast] = useState<string | null>(null);

    const showAppToast = (msg: string) => {
        setAppToast(msg);
        setTimeout(() => setAppToast(null), 5000);
    };

    const handleSubscribe = async (type: 'one-time' | 'monthly' | 'metered' = 'one-time') => {
        try {
            const response = await fetch(`${BACKEND_URL}/checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token: 'slablogic-alpha',
                    billingType: type
                })
            });
            const data = await response.json();
            if (data.url) {
                window.location.href = data.url;
            } else {
                showAppToast("Notice: Public token-saver mode active. Billing checkout is offline.");
            }
        } catch (e) {
            showAppToast("Notice: Self-serve billing gateway unwired (Token Conservation Mode).");
        }
    };

    const [isLoggingInAsTester, setIsLoggingInAsTester] = useState(false);
    const handleTesterLogin = async () => {
        setIsLoggingInAsTester(true);
        try {
            // Force sign out first to clear any stale session
            try { await signOut(); } catch (e) { /* ignore */ }

            // This endpoint returns credentials for a temporary testing account
            const response = await fetch(`${BACKEND_URL}/testing/session`);
            const data = await response.json();
            if (data.username && data.password) {
                await signIn({
                    username: data.username,
                    password: data.password
                });
                setShowAuth(false);
            } else {
                showAppToast("Test session unavailable: Running in guest evaluation mode.");
            }
        } catch (e: any) {
            console.error("Tester login error:", e);
            showAppToast("Guest evaluation mode active.");
        } finally {
            setIsLoggingInAsTester(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950/20 to-slate-900 text-slate-100 pb-20 font-sans selection:bg-indigo-500/30">
            {/* App Toast Banner */}
            {appToast && (
                <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-3 bg-indigo-600/95 text-white text-xs font-bold rounded-2xl shadow-2xl border border-indigo-400/50 flex items-center gap-3 backdrop-blur-md animate-fadeIn">
                    <span>{appToast}</span>
                    <button onClick={() => setAppToast(null)} className="text-indigo-200 hover:text-white font-bold ml-2">✕</button>
                </div>
            )}

            {/* Top Banner for Theirloom */}
            <div className="w-full bg-gradient-to-r from-indigo-950 via-rose-950/40 to-indigo-950 border-b border-rose-500/20 py-2.5 px-4 text-center text-sm relative z-20 backdrop-blur-sm">
                <a 
                    href="https://theirloom.io" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-rose-200 hover:text-white transition-colors font-medium tracking-wide"
                >
                    <Sparkles className="w-4 h-4 text-rose-400 animate-pulse" />
                    <span>Weave historic archives & dry transcripts into graphic novels. Experience <strong>Theirloom</strong>, SlabLogic's Flagship AI Storytelling platform →</span>
                </a>
            </div>

            <Hero />

            {/* User Bar */}
            <div className="bg-slate-900 text-white text-xs py-1 px-4 flex justify-between items-center">
                <span>
                    {user ? `Logged in as: ${user?.signInDetails?.loginId || user?.username}` : <span className="text-yellow-400 font-bold">Guest Mode - 1 Free Scan Available</span>}
                </span>
                <div className="flex items-center gap-4">
                    {!user && (
                        <button onClick={() => setShowAuth(true)} className="text-blue-300 hover:text-white transition-colors">
                            Sign In
                        </button>
                    )}
                    <button onClick={() => setShowPricing(true)} className="text-yellow-400 hover:text-yellow-300 font-bold flex items-center gap-1 transition-colors">
                        <Star className="w-3 h-3" /> Upgrade to Pro
                    </button>
                    {user && (
                        <button onClick={signOut} className="flex items-center gap-1 hover:text-red-300 transition-colors">
                            <LogOut size={12} /> Sign Out
                        </button>
                    )}
                </div>
            </div>

            {/* Navigation (Hidden on Landing Page) */}
            {location.pathname !== '/' && (
                <div className="sticky top-0 z-30 bg-slate-900/60 backdrop-blur-xl py-4 border-b border-slate-800 shadow-xl mb-8">
                    <div className="max-w-7xl mx-auto px-4 flex justify-center">
                        <div className="bg-slate-800/50 p-1.5 rounded-2xl shadow-inner border border-slate-700/50 inline-flex backdrop-blur-md">
                            <Link to="/anti-slop" className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold transition-all duration-300 ${location.pathname.startsWith('/anti-slop') ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.4)]' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'}`}>
                                <Sparkles className="w-4 h-4" /> Anti-Slop Filter
                            </Link>
                            <Link to="/game-maker" className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold transition-all duration-300 ${location.pathname.startsWith('/game-maker') ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.4)]' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'}`}>
                                <Sparkles className="w-4 h-4" /> Game Maker
                            </Link>
                            <Link to="/workspace/scan" className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold transition-all duration-300 ${location.pathname.includes('/scan') || location.pathname === '/workspace' ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.4)]' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'}`}>
                                <Grid className="w-4 h-4" /> Scan
                            </Link>
                            <Link to="/workspace/search" className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold transition-all duration-300 ${location.pathname.includes('/search') ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.4)]' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'}`}>
                                <SearchIcon className="w-4 h-4" /> Search
                            </Link>
                            <Link to="/workspace/collection" className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold transition-all duration-300 ${location.pathname.includes('/collection') ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.4)]' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'}`}>
                                <Layers className="w-4 h-4" /> My Slabs
                                {savedCards.length > 0 && <span className="ml-1.5 text-[10px] bg-indigo-500 text-white px-2 py-0.5 rounded-full shadow-lg">{savedCards.length}</span>}
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* LOADING OVERLAY (Knowledge Graph) */}
                {isLoading && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/90 backdrop-blur-sm">
                        <div className="relative max-w-2xl w-full mx-4 aspect-video rounded-2xl overflow-hidden shadow-2xl border border-slate-700">
                            {/* VIBE ASSET: Knowledge Graph */}
                            <img
                                src="/workspace/assets/vibe/005_the_knowledge_graph_node_map.jpeg"
                                alt="RAG Processing"
                                className="absolute inset-0 w-full h-full object-cover opacity-80 animate-pulse-slow"
                            />

                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-slate-900/50"></div>

                            <div className="relative z-10 flex flex-col items-center justify-center h-full text-center">
                                <Loader2 className="w-12 h-12 text-blue-400 animate-spin mb-4 drop-shadow-[0_0_15px_rgba(96,165,250,0.5)]" />
                                <h2 className="text-2xl font-bold text-white tracking-widest mb-2">NEURAL SCANNING</h2>
                                <p className="text-blue-200 font-mono text-sm bg-slate-900/50 px-4 py-1 rounded-full border border-blue-500/30">
                                    {analysisState.message || "Consulting Vintage/Modern Knowledge Base..."}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <Routes>
                    <Route path="/" element={
                        <div className="py-12 flex flex-col items-center gap-8">
                            <h2 className="text-3xl font-bold text-white tracking-wide">Select Your Workspace</h2>
                            <div className="flex flex-wrap justify-center gap-6">
                                <Link to="/workspace/scan" className="px-8 py-5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl shadow-xl transition-all hover:scale-[1.03] flex items-center gap-2.5">
                                    <Grid className="w-5 h-5" /> Trading Cards Forensic DB
                                </Link>
                                <Link to="/anti-slop" className="px-8 py-5 bg-purple-900 hover:bg-purple-800 text-white font-bold rounded-2xl shadow-xl transition-all hover:scale-[1.03] flex items-center gap-2.5">
                                    <Sparkles className="w-5 h-5 text-purple-400" /> Veritas Anti-Slop Filter
                                </Link>
                                <Link to="/game-maker" className="px-8 py-5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-2xl shadow-xl transition-all hover:scale-[1.03] flex items-center gap-2.5">
                                    <Sparkles className="w-5 h-5 text-indigo-400" /> Game Maker Studio
                                </Link>
                                <a href="https://theirloom.io" target="_blank" rel="noopener noreferrer" className="px-8 py-5 bg-gradient-to-br from-rose-900 to-rose-950 hover:from-rose-800 hover:to-rose-900 text-white font-bold rounded-2xl shadow-[0_10px_30px_rgba(244,63,94,0.15)] border border-rose-500/30 transition-all hover:scale-[1.03] flex items-center gap-2.5">
                                    <Sparkles className="w-5 h-5 text-rose-400 animate-pulse" /> Theirloom Flagship Studio →
                                </a>
                                <a href="https://mealswipe.mom" target="_blank" rel="noopener noreferrer" className="px-8 py-5 bg-gradient-to-br from-green-900 to-green-950 hover:from-green-800 hover:to-green-900 text-white font-bold rounded-2xl shadow-xl border border-green-500/30 transition-all hover:scale-[1.03] flex items-center gap-2.5">
                                    <Utensils className="w-5 h-5 text-green-400" /> MealSwipe →
                                </a>
                                <a href="https://dialected.io" target="_blank" rel="noopener noreferrer" className="px-8 py-5 bg-gradient-to-br from-sky-900 to-sky-950 hover:from-sky-800 hover:to-sky-900 text-white font-bold rounded-2xl shadow-xl border border-sky-500/30 transition-all hover:scale-[1.03] flex items-center gap-2.5">
                                    <GraduationCap className="w-5 h-5 text-sky-400" /> DialectEd →
                                </a>
                                <a href="/chronos/" className="px-8 py-5 bg-gradient-to-br from-rose-950 to-slate-900 hover:from-rose-900 hover:to-slate-800 text-white font-bold rounded-2xl shadow-xl border border-rose-500/30 transition-all hover:scale-[1.03] flex items-center gap-2.5">
                                    <Terminal className="w-5 h-5 text-rose-400" /> Chronos Diaries →
                                </a>
                            </div>
                        </div>
                    } />
                    <Route path="/anti-slop" element={<AntiSlop />} />
                    <Route path="/game-maker" element={<GameMaker />} />
                    <Route path="/workspace" element={<Navigate to="/workspace/scan" replace />} />
                    <Route path="/workspace/scan" element={
                        !isLoading ? (
                            <div className="mb-12">
                                <div className="flex justify-center mb-4">
                                    <div className="bg-white p-1 rounded-xl shadow-sm border border-slate-200 inline-flex">
                                        <button onClick={() => setScanMode('single')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${scanMode === 'single' ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                                            <Scan className="w-4 h-4" /> Single Card
                                        </button>
                                        <button onClick={() => setScanMode('batch')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${scanMode === 'batch' ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                                            <Layers className="w-4 h-4" /> Batch (5-10)
                                        </button>
                                    </div>
                                </div>
                                {scanMode === 'batch' ? (
                                    <BatchUpload onImagesSelected={handleImageSelected} isLoading={false} maxImages={10} />
                                ) : (
                                    <>
                                        <div className="flex justify-center mb-6">
                                            <div className="bg-slate-100 p-1 rounded-lg inline-flex">
                                                <button onClick={() => setScanType('quick')} className={`px-3 py-1 rounded text-xs font-medium transition-all ${scanType === 'quick' ? 'bg-white shadow text-indigo-700' : 'text-slate-500'}`}>Quick (Front)</button>
                                                <button onClick={() => setScanType('pro')} className={`px-3 py-1 rounded text-xs font-medium transition-all ${scanType === 'pro' ? 'bg-white shadow text-indigo-700' : 'text-slate-500'}`}>Pro (Front+Back)</button>
                                            </div>
                                        </div>
                                        <ImageInput onImagesSelected={handleImageSelected} isLoading={false} progress={analysisState.progress} statusMessage={analysisState.message} ocrText={analysisState.ocrText} mode={'precision'} scanType={scanType} />
                                    </>
                                )}
                            </div>
                        ) : null
                    } />
                    <Route path="/workspace/search" element={
                        <div className="mb-12 max-w-xl mx-auto space-y-6">
                            <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
                                <h3 className="text-xl font-bold text-slate-800 mb-4 text-center">Card Database</h3>
                                <div className="flex gap-2">
                                    <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="flex-1 border border-slate-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="e.g. 1986 Fleer Michael Jordan" />
                                    <button onClick={handleManualSearch} disabled={isLoading} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-6 font-medium">
                                        {isLoading ? <Loader2 className="animate-spin" /> : 'Go'}
                                    </button>
                                </div>
                            </div>

                            {/* Theirloom Search Page Promotion */}
                            <div className="bg-slate-950/60 border border-rose-500/30 rounded-3xl p-5 shadow-[0_10px_30px_rgba(244,63,94,0.15)] text-center animate-in fade-in duration-500">
                                <p className="text-xs text-slate-400 leading-relaxed">
                                    💡 <strong>Legacy Tip:</strong> Looking up cards with deep sentimental value? Use <a href="https://theirloom.io" target="_blank" rel="noopener noreferrer" className="text-rose-450 font-bold hover:underline">Theirloom</a> to preserve the history of your collection as an illustrated archival legacy book.
                                </p>
                            </div>
                        </div>
                    } />
                    <Route path="/workspace/collection" element={<></>} />
                </Routes>

                {/* Listing Results - Visible across all tradingcards routes when cards are loaded */}
                {location.pathname.startsWith('/workspace') && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {displayedCards.map((card) => (
                            <CardSlab key={card.id} card={card} onList={(c) => setSelectedCardForListing(c)} isSaved={savedCards.some(s => s.id === card.id)} onToggleSave={toggleSaveCrad} />
                        ))}
                    </div>
                )}

                {/* Contextual Theirloom Heritage Timelines CTA */}
                {location.pathname.startsWith('/workspace') && displayedCards.length > 0 && (
                    <div className="mt-12 bg-slate-950/60 border border-rose-500/30 rounded-3xl p-6 shadow-[0_10px_30px_rgba(244,63,94,0.15)] flex flex-col md:flex-row items-center justify-between gap-6 animate-in fade-in duration-500">
                        <div className="flex items-center gap-4 text-center md:text-left">
                            <div className="p-3 bg-rose-500/10 rounded-2xl border border-rose-500/20 text-rose-400 shrink-0">
                                <Sparkles className="w-6 h-6 animate-pulse" />
                            </div>
                            <div>
                                <h4 className="font-bold text-base text-slate-200">Preserve the Legacy of Your Card Collection</h4>
                                <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                                    Card collections are built on memories and family history. Beyond forensic grading, weave your card logs, player stats, and historical timelines into a premium illustrated graphic novel or digital legacy dossier with <strong className="text-rose-400 font-semibold">Theirloom</strong>.
                                </p>
                            </div>
                        </div>
                        <a 
                            href="https://theirloom.io" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-rose-600 to-rose-700 hover:from-rose-500 hover:to-rose-600 text-white text-sm font-bold rounded-xl shadow-lg shadow-rose-950/50 flex items-center justify-center gap-2 transition-all hover:scale-[1.03] shrink-0"
                        >
                            Build Heritage Dossier →
                        </a>
                    </div>
                )}

                {displayedCards.length === 0 && !isLoading && location.pathname === '/workspace/collection' && (
                    <div className="text-center text-slate-400 py-12 flex flex-col items-center">
                        <div className="w-64 h-48 rounded-2xl overflow-hidden shadow-inner border border-slate-700 mb-6 relative group">
                            <img src="/workspace/assets/vibe/010_the_dark_inventory_discovery.jpeg" alt="No Inventory" className="w-full h-full object-cover opacity-60 grayscale group-hover:grayscale-0 transition-all duration-700" />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="bg-slate-900/80 px-4 py-2 rounded-full border border-slate-600 text-xs font-mono text-slate-300">INVENTORY: EMPTY</div>
                            </div>
                        </div>
                        <h3 className="text-lg font-medium text-slate-300">Your Slab Locker is Empty</h3>
                        <p className="text-slate-500 mb-6 max-w-sm">Start scanning your raw cards to generate forensic grades and unlock market value.</p>
                        <Link to="/workspace/scan" className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-lg font-bold transition-colors shadow-lg shadow-blue-900/20">
                            Launch Scanner
                        </Link>
                    </div>
                )}
            </div>

            {
                selectedCardForListing && (
                    <ListingModal
                        card={selectedCardForListing as any}
                        onClose={() => setSelectedCardForListing(null)}
                    />
                )
            }

            {/* Pricing Modal */}
            {showPricing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl p-6 shadow-2xl relative max-w-lg w-full">
                        <button onClick={() => setShowPricing(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
                            <X className="w-6 h-6" />
                        </button>
                        <h2 className="text-2xl font-bold text-slate-800 text-center mb-6">Choose Your Plan</h2>

                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                            <div className="border border-slate-200 rounded-xl p-3 flex flex-col items-center hover:border-indigo-500 transition-all cursor-pointer group"
                                onClick={() => handleSubscribe('one-time')}>
                                <div className="text-[10px] font-bold text-slate-400 uppercase mb-1">20-Scan Pack</div>
                                <div className="text-2xl font-extrabold text-slate-900 mb-1">$4.99</div>
                                <div className="text-[10px] text-slate-500 mb-3 text-center">Small batch check</div>
                                <button className="mt-auto w-full py-1.5 bg-slate-100 group-hover:bg-indigo-600 group-hover:text-white rounded-lg text-[10px] font-bold transition-all">Select</button>
                            </div>

                            {/* Monthly */}
                            <div className="border-2 border-indigo-600 rounded-xl p-3 flex flex-col items-center shadow-lg relative bg-indigo-50/30 group cursor-pointer"
                                onClick={() => handleSubscribe('monthly')}>
                                <div className="absolute -top-3 bg-indigo-600 text-white text-[8px] px-2 py-0.5 rounded-full font-bold">BEST VALUE</div>
                                <div className="text-[10px] font-bold text-indigo-600 uppercase mb-1">Pro Monthly</div>
                                <div className="text-2xl font-extrabold text-slate-900 mb-1">$29</div>
                                <div className="text-[10px] text-slate-500 mb-3 text-center">Unlimited Scans</div>
                                <button className="mt-auto w-full py-1.5 bg-indigo-600 text-white rounded-lg text-[10px] font-bold transition-all">Subscribe</button>
                            </div>

                            {/* Veritas Anti-Slop Lifetime Plan */}
                            <div className="border border-slate-200 rounded-xl p-3 flex flex-col items-center hover:border-purple-500 transition-all cursor-pointer group"
                                onClick={() => handleSubscribe('veritas-antislop')}>
                                <div className="text-[10px] font-bold text-slate-400 uppercase mb-1">Veritas Filter</div>
                                <div className="text-2xl font-extrabold text-slate-900 mb-1">$100</div>
                                <div className="text-[10px] text-slate-500 mb-3 text-center">Lifetime AI writing compiler</div>
                                <button className="mt-auto w-full py-1.5 bg-slate-100 group-hover:bg-purple-600 group-hover:text-white rounded-lg text-[10px] font-bold transition-all">Select</button>
                            </div>

                            {/* Metered */}
                            <div className="border border-slate-200 rounded-xl p-3 flex flex-col items-center hover:border-indigo-500 transition-all cursor-pointer group"
                                onClick={() => handleSubscribe('metered')}>
                                <div className="text-[10px] font-bold text-slate-400 uppercase mb-1">Enterprise</div>
                                <div className="text-2xl font-extrabold text-slate-900 mb-1">$0.25</div>
                                <div className="text-[10px] text-slate-500 mb-3 text-center">Pay-as-you-go (per scan)</div>
                                <button className="mt-auto w-full py-1.5 bg-slate-100 group-hover:bg-indigo-600 group-hover:text-white rounded-lg text-[10px] font-bold transition-all">Select</button>
                            </div>
                        </div>

                        <p className="mt-6 text-center text-[10px] text-slate-400">
                            Secure payments by Stripe. Cancel subscriptions anytime from your dashboard.
                        </p>
                    </div>
                </div>
            )}

            {/* Auth Overlay */}
            {showAuth && !user && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl p-6 shadow-2xl relative max-w-md w-full animate-in fade-in zoom-in duration-200">
                        <button
                            onClick={() => setShowAuth(false)}
                            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-1"
                        >
                            <X className="w-6 h-6" />
                        </button>
                        <div className="mb-6 text-center">
                            <h2 className="text-2xl font-bold text-slate-800">Welcome Back</h2>
                            <p className="text-slate-500 text-sm">Sign in to manage your slab collection</p>
                        </div>

                        <div className="mb-6">
                            <button
                                onClick={handleTesterLogin}
                                disabled={isLoggingInAsTester}
                                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-indigo-200"
                            >
                                {isLoggingInAsTester ? <Loader2 className="w-5 h-5 animate-spin" /> : <Star className="w-5 h-5 fill-white" />}
                                Login as Tester (One-Click)
                            </button>
                            <div className="flex items-center my-4">
                                <div className="flex-1 border-t border-slate-200"></div>
                                <span className="px-3 text-slate-400 text-xs font-medium uppercase tracking-wider">or use your account</span>
                                <div className="flex-1 border-t border-slate-200"></div>
                            </div>
                        </div>

                        <Authenticator />
                    </div>
                </div>
            )}
        </div >
    );
};

const App: React.FC = () => {
    return (
        <Authenticator.Provider>
            <HashRouter>
                <AppContent />
            </HashRouter>
        </Authenticator.Provider>
    );
};

export default App;